/**
 * Pure helpers for rendering task-table cells.
 * Kept framework-free so the decision logic can be unit-tested
 * without mounting a grid component.
 */

export function stampName(person) {
  return (person && person.name) || "";
}

export function stampAvatar(person) {
  return (person && person.avatar) || "";
}

export function stampAssignedDate(row) {
  if (row.assignedBy && row.assignedBy.date != null && row.assignedBy.date !== "") {
    return row.assignedBy.date;
  }
  return row.assignedOn;
}

export function formatAssignedDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = date.toLocaleString("default", { month: "short" });
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Date-only formatter for the To Do / Planned / In Progress status line.
 * Produces "Jan 1, 2024" (English month, no leading zero, no time).
 * Date-only "YYYY-MM-DD" inputs are parsed by parts so the day is never
 * shifted by a local timezone offset.
 */
export function formatDateOnly(value) {
  if (!value) return "";
  const raw = typeof value === "string" ? value.trim() : value;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    if (!year || !month || !day) return "";
    return `${MONTH_NAMES[month - 1]} ${day}, ${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * Second line under the Status badge.
 * - to_do / planned / in_progress -> "By {deadline}" (date only), omitted when
 *   there is no deadline.
 * - completed / canceled -> "On {doneOn}" (date + time) via formatAssignedDate,
 *   omitted when there is no doneOn.
 */
export function resolveStatusDateLabel(row) {
  const status = ((row && row.status) || "").toLowerCase().replace(/\s+/g, "_");
  if (["to_do", "planned", "in_progress"].includes(status)) {
    if (!row || !row.deadline) return "";
    return `By ${formatDateOnly(row.deadline)}`;
  }
  if (["completed", "canceled"].includes(status)) {
    if (!row || !row.doneOn) return "";
    return `On ${formatAssignedDate(row.doneOn)}`;
  }
  return "";
}

const TASK_TYPE_LABEL = {
  upload_microlearning_video: "Upload Microlearning video to checkpoint",
  register_tutorial_qr_code: "Register QR code to checkpoint",
  register_qr: "Register QR code to checkpoint",
};

/**
 * Human-readable task type for the Summary first line (Figma).
 */
export function formatTaskTypeLabel(task) {
  const type = String((task && task.type) || "").trim();
  const typeKey = type.toLowerCase();
  if (TASK_TYPE_LABEL[typeKey]) return TASK_TYPE_LABEL[typeKey];
  if (typeKey.includes("qr") || typeKey.includes("register")) {
    return TASK_TYPE_LABEL.register_qr;
  }
  if (typeKey.includes("upload") || typeKey.includes("microlearning")) {
    return TASK_TYPE_LABEL.upload_microlearning_video;
  }
  const summary = String((task && task.summary) || "").trim();
  if (summary) return summary;
  return type.replace(/_/g, " ").trim();
}

/**
 * First Summary line: type, then criteria.
 */
export function summaryFirstLine(row) {
  const typeLabel = formatTaskTypeLabel(row);
  const criteria = row && String(row.criteria || "").trim();
  if (typeLabel && criteria) {
    if (typeLabel.toLowerCase().includes(criteria.toLowerCase())) return typeLabel;
    return `${typeLabel} ${criteria}`;
  }
  return typeLabel || criteria || "";
}

/**
 * Picks the type-icon kind for a row. Upload-video tasks keep the upload icon
 * in every status. Other tasks use check when done or canceled, qr for register.
 */
export function resolveTaskIconKind(task) {
  const status = ((task && task.status) || "").toLowerCase().replace(/\s+/g, "_");
  const type = (task && task.type) || "";
  if (type.includes("upload")) return "upload";
  if (status === "completed" || status === "canceled") return "check";
  if (type.includes("qr") || type.includes("register")) return "qr";
  return "upload";
}

/** Identifies an "Upload Video" task by type (matches the existing icon logic). */
export function isUploadVideoTask(task) {
  const type = String((task && task.type) || "");
  return type.includes("upload") || type.includes("microlearning");
}

const PENDING_STATUS_KEYS = new Set(["to_do", "in_progress", "planned"]);

/**
 * Pending in the Gherkin sense: API status PENDING collapses the UI keys
 * to_do / in_progress / planned onto the same wire value. Only pending
 * upload-video tasks get the lifecycle menu.
 */
export function isPendingUploadVideoTask(task) {
  if (!isUploadVideoTask(task)) return false;
  const status = String((task && task.status) || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  return PENDING_STATUS_KEYS.has(status);
}

export const TASK_MENU_ACTION = Object.freeze({
  CANCEL: "cancel",
  REASSIGN: "reassign",
  SEND_REMINDER: "send_reminder",
});

/** Canonical row-menu items for a pending upload-video task (ER-11866). */
export const TASK_MENU_ITEMS = Object.freeze([
  { name: "Cancel", action: TASK_MENU_ACTION.CANCEL, destructive: true },
  { name: "Reassign", action: TASK_MENU_ACTION.REASSIGN },
  { name: "Send Reminder", action: TASK_MENU_ACTION.SEND_REMINDER },
]);

/**
 * Row-menu items for a task. Returns the available items only for pending
 * upload-video tasks; every other row gets an empty menu. `availableItems`
 * lets a consumer gate the whole menu (empty array -> no items render).
 */
export function taskMenuItems(task, availableItems = TASK_MENU_ITEMS) {
  if (!isPendingUploadVideoTask(task)) return [];
  return availableItems || [];
}

/**
 * Resolves what the "Assigned To" cell should render for a task row: assignee
 * name/avatar with the same assigned-on timestamp as Assigned By/On.
 */
export function resolveAssignedToCell(row) {
  const assignee = row && row.assignedTo;
  return {
    name: (assignee && assignee.name) || "",
    avatar: (assignee && assignee.avatar) || "",
    date: stampAssignedDate(row),
  };
}

/**
 * Resolves what the "Assigned By/On" cell should render for a task row.
 * Returns a Stamp descriptor when an assigner name is available, otherwise
 * a date-only descriptor built from the assigned-on date.
 */
export function resolveAssignedByCell(row) {
  const name = stampName(row && row.assignedBy);
  const date = stampAssignedDate(row);
  if (name) {
    return {
      type: "stamp",
      name,
      avatar: stampAvatar(row.assignedBy),
      date,
    };
  }
  return {
    type: "date",
    dateLabel: formatAssignedDate(date),
  };
}
